import { colors } from '@factorialco/deprecated-design-system'

import { PlotData } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import { PaginatedTableItem } from 'modules/finance/components/FinancialWorkspace/types/table'

import { ComboChartData } from './ComboChart/types'

export interface PARSED_ITEMS {
  LABOR_COST?: PlotData[]
  COMPENSATIONS?: PlotData[]
  AGREEMENT_DATA?: PlotData[]
  TOTAL_EXPENSES?: PlotData[]
  EMPLOYEE_EXPENSES?: PlotData[]
  COMPARISON_LABOR_COST?: PlotData[]
  COMPARISON_COMPENSATIONS?: PlotData[]
  COMPARISON_AGREEMENT_DATA?: PlotData[]
  COMPARISON_TOTAL_EXPENSES?: PlotData[]
  COMPARISON_EMPLOYEE_EXPENSES?: PlotData[]
}

enum SOURCE_KEYS {
  payroll_data = 'payroll_data',
  expenses_data = 'expenses_data',
  agreements_data = 'agreements_data',
  compensation_data = 'compensation_data',
}

const dataSourceKey = (itemKey: string, comparisonKey?: boolean) => {
  if (comparisonKey) {
    if (itemKey === SOURCE_KEYS.payroll_data) return 'COMPARISON_LABOR_COST'
    if (itemKey === SOURCE_KEYS.agreements_data) return 'COMPARISON_AGREEMENT_DATA'
    if (itemKey === SOURCE_KEYS.expenses_data) return 'COMPARISON_EMPLOYEE_EXPENSES'
    if (itemKey === SOURCE_KEYS.compensation_data) return 'COMPARISON_COMPENSATIONS'
  }

  if (itemKey === SOURCE_KEYS.payroll_data) return 'LABOR_COST'
  if (itemKey === SOURCE_KEYS.agreements_data) return 'AGREEMENT_DATA'
  if (itemKey === SOURCE_KEYS.expenses_data) return 'EMPLOYEE_EXPENSES'
  if (itemKey === SOURCE_KEYS.compensation_data) return 'COMPENSATIONS'
}

export const parseItems = (
  items: PaginatedTableItem[],
  labels: string[],
  comparisonData?: boolean
) => {
  const parsedItems: PARSED_ITEMS = {}

  items.forEach((item) => {
    if (!item.loading) {
      const key = dataSourceKey(`${item.id}`, comparisonData)

      if (key) {
        const data = comparisonData ? item.comparisonData : item.data
        parsedItems[key] = data?.map(
          ({ label, totalAmountInCents, totalAmountWithPrecision }, index) => ({
            title: label,
            totalAmountWithPrecision,
            y: Number(totalAmountInCents) || null,
            x: labels[index] || label.slice(0, -5),
          })
        )
      }
    }
  })

  if (comparisonData) {
    const employeeCostData =
      parsedItems['COMPARISON_LABOR_COST'] || parsedItems['COMPARISON_COMPENSATIONS']
    if (employeeCostData && parsedItems['COMPARISON_EMPLOYEE_EXPENSES']) {
      parsedItems['COMPARISON_TOTAL_EXPENSES'] = employeeCostData.map((obj, idx) => {
        return {
          ...obj,
          y: (obj.y || 0) + (parsedItems['COMPARISON_EMPLOYEE_EXPENSES']?.[idx]?.y || 0),
        }
      })
    }
  } else {
    const employeeCostData = parsedItems['LABOR_COST'] || parsedItems['COMPENSATIONS']
    if (employeeCostData && parsedItems['EMPLOYEE_EXPENSES']) {
      parsedItems['TOTAL_EXPENSES'] = employeeCostData.map((obj, idx) => {
        return { ...obj, y: (obj.y || 0) + (parsedItems['EMPLOYEE_EXPENSES']?.[idx]?.y || 0) }
      })
    }
  }

  return parsedItems
}

export const computeTotal = (data?: PlotData[]) => {
  return (data || []).reduce(
    (accumulator, currentValue) =>
      accumulator + Number(currentValue.totalAmountWithPrecision || currentValue.y || 0),
    0
  )
}

export enum SerieIds {
  LaborCost = 'laborCost',
  AgreementData = 'agreementData',
  TotalExpenses = 'totalExpenses',
  EmployeeExpenses = 'employeeExpenses',
  ComparisonLaborCost = 'comparisonLaborCost',
  ComparisonAgreementData = 'comparisonAgreementData',
  ComparisonTotalExpenses = 'comparisonTotalExpenses',
  ComparisonEmployeeExpenses = 'comparisonEmployeeExpenses',
}

export const buildComboChartData = (parsedItems: PARSED_ITEMS): ComboChartData[] => {
  const data = []
  const {
    LABOR_COST,
    COMPENSATIONS,
    AGREEMENT_DATA,
    TOTAL_EXPENSES,
    EMPLOYEE_EXPENSES,
    COMPARISON_LABOR_COST,
    COMPARISON_COMPENSATIONS,
    COMPARISON_AGREEMENT_DATA,
    COMPARISON_TOTAL_EXPENSES,
    COMPARISON_EMPLOYEE_EXPENSES,
  } = parsedItems

  if (COMPARISON_TOTAL_EXPENSES) {
    data.push({
      data: [...COMPARISON_TOTAL_EXPENSES],
      id: SerieIds.ComparisonTotalExpenses,
      color: colors.viridian1000,
    })
  }

  if (COMPARISON_AGREEMENT_DATA) {
    data.push({
      data: [...COMPARISON_AGREEMENT_DATA],
      id: SerieIds.ComparisonAgreementData,
      color: 'rgba(81,81,100,1)',
    })
  }

  if (COMPARISON_EMPLOYEE_EXPENSES) {
    data.push({
      data: [...COMPARISON_EMPLOYEE_EXPENSES],
      id: SerieIds.ComparisonEmployeeExpenses,
      color: 'rgba(245,165,28,1)',
    })
  }

  if (COMPARISON_LABOR_COST) {
    data.push({
      data: [...COMPARISON_LABOR_COST],
      id: SerieIds.ComparisonLaborCost,
      color: 'rgba(85,150,246,1)',
    })
  }

  if (TOTAL_EXPENSES) {
    data.push({
      data: [...TOTAL_EXPENSES],
      id: SerieIds.TotalExpenses,
      color: colors.viridian1000,
    })
  }

  if (AGREEMENT_DATA) {
    data.push({
      data: [...AGREEMENT_DATA],
      id: SerieIds.AgreementData,
      color: 'rgba(81,81,100,1)',
    })
  }

  if (EMPLOYEE_EXPENSES) {
    data.push({
      data: [...EMPLOYEE_EXPENSES],
      id: SerieIds.EmployeeExpenses,
      color: 'rgba(245,165,28,1)',
    })
  }

  if (LABOR_COST) {
    data.push({
      data: [...LABOR_COST],
      id: SerieIds.LaborCost,
      color: 'rgba(85,150,246,1)',
    })
  }

  return data
}

export interface MyBarDatum {
  x: string
  [key: string]: string | number
}

export const hasNegativeValue = (chartData?: ComboChartData[]) => {
  return chartData?.some(({ data }) => {
    return data.some(({ y }) => y && y < 0)
  })
}

export const highestDataPoint = (chartData?: ComboChartData[]) => {
  const highestChartData = chartData?.reduce((maxChartData, currentChartData) => {
    return Math.abs(
      currentChartData.data.reduce(
        (maxCurrent, currentCurrent) =>
          Math.abs(currentCurrent?.y || 0) > Math.abs(maxCurrent?.y || 0)
            ? currentCurrent
            : maxCurrent,
        currentChartData.data[0]
      )?.y || 0
    ) >
      Math.abs(
        maxChartData?.data.reduce(
          (maxMax, currentMax) =>
            Math.abs(currentMax.y || 0) > Math.abs(maxMax?.y || 0) ? currentMax : maxMax,
          maxChartData.data[0]
        )?.y || 0
      )
      ? currentChartData
      : maxChartData
  }, chartData[0])

  return highestChartData?.data?.reduce(
    (maxData, currentData) =>
      Math.abs(currentData.y || 0) > Math.abs(maxData?.y || 0) ? currentData : maxData,
    highestChartData?.data?.[0]
  )
}

// KEEPING THESE HERE AS DUMMY DATA
// const AGREEMENT_DATA: PlotData[] = [
//   { x: 'Jan', title: 'Jan 2025', y: 10665811, totalAmountWithPrecision: '10665811' },
//   { x: 'Feb', title: 'Feb 2025', y: 20120198, totalAmountWithPrecision: '20120198' },
//   { x: 'Mar', title: 'Mar 2025', y: 2326830, totalAmountWithPrecision: '2326830' },
//   { x: 'Apr', title: 'Apr 2025', y: 25326830, totalAmountWithPrecision: '25326830' },
//   { x: 'May', title: 'May 2025', y: 14712957, totalAmountWithPrecision: '14712957' },
//   { x: 'Jun', title: 'Jun 2025', y: 8064308, totalAmountWithPrecision: '8064308' },
//   { x: 'Jul', title: 'Jul 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Aug', title: 'Aug 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Sep', title: 'Sep 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Oct', title: 'Oct 2025', y: 22797559, totalAmountWithPrecision: '22797559' },
//   { x: 'Nov', title: 'Nov 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Dec', title: 'Dec 2025', y: null, totalAmountWithPrecision: '0.0' },
// ]

// const LABOR_COST: PlotData[] = [
//   { x: 'Jan', title: 'Jan 2025', y: 3132498, totalAmountWithPrecision: '3132498' },
//   { x: 'Feb', title: 'Feb 2025', y: 14096294, totalAmountWithPrecision: '14096294' },
//   { x: 'Mar', title: 'Mar 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Apr', title: 'Apr 2025', y: 12203715, totalAmountWithPrecision: '12203715' },
//   { x: 'May', title: 'May 2025', y: 3496079, totalAmountWithPrecision: '3496079' },
//   { x: 'Jun', title: 'Jun 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Jul', title: 'Jul 2025', y: 1364116, totalAmountWithPrecision: '1364116' },
//   { x: 'Aug', title: 'Aug 2025', y: 14109652, totalAmountWithPrecision: '14109652' },
//   { x: 'Sep', title: 'Sep 2025', y: 4418305, totalAmountWithPrecision: '4418305' },
//   { x: 'Oct', title: 'Oct 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Nov', title: 'Nov 2025', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Dec', title: 'Dec 2025', y: null, totalAmountWithPrecision: '0.0' },
// ]

// const EMPLOYEE_EXPENSES: PlotData[] = [
//   { x: 'Jan', title: 'Jan 2025', y: 4779860, totalAmountWithPrecision: '4779860' },
//   { x: 'Feb', title: 'Feb 2025', y: 7810441, totalAmountWithPrecision: '7810441' },
//   { x: 'Mar', title: 'Mar 2025', y: 5054227, totalAmountWithPrecision: '5054227' },
//   { x: 'Apr', title: 'Apr 2025', y: 15215243, totalAmountWithPrecision: '15215243' },
//   { x: 'May', title: 'May 2025', y: 13609412, totalAmountWithPrecision: '13609412' },
//   { x: 'Jun', title: 'Jun 2025', y: 14944125, totalAmountWithPrecision: '14944125' },
//   { x: 'Jul', title: 'Jul 2025', y: 13960775, totalAmountWithPrecision: '13960775' },
//   { x: 'Aug', title: 'Aug 2025', y: 16409776, totalAmountWithPrecision: '16409776' },
//   { x: 'Sep', title: 'Sep 2025', y: 8842962, totalAmountWithPrecision: '8842962' },
//   { x: 'Oct', title: 'Oct 2025', y: 14991789, totalAmountWithPrecision: '14991789' },
//   { x: 'Nov', title: 'Nov 2025', y: 13549979, totalAmountWithPrecision: '13549979' },
//   { x: 'Dec', title: 'Dec 2025', y: 1027409, totalAmountWithPrecision: '1027409' },
// ]

// const COMPARISON_AGREEMENT_DATA: PlotData[] = [
//   { x: 'Jan', title: 'Jan 2024', y: 2258691, totalAmountWithPrecision: '2258691' },
//   { x: 'Feb', title: 'Feb 2024', y: 5706123, totalAmountWithPrecision: '5706123' },
//   { x: 'Mar', title: 'Mar 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Apr', title: 'Apr 2024', y: 20012522, totalAmountWithPrecision: '2912522' },
//   { x: 'May', title: 'May 2024', y: 16346570, totalAmountWithPrecision: '16346570' },
//   { x: 'Jun', title: 'Jun 2024', y: 3556567, totalAmountWithPrecision: '3556567' },
//   { x: 'Jul', title: 'Jul 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Aug', title: 'Aug 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Sep', title: 'Sep 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Oct', title: 'Oct 2024', y: 12302139, totalAmountWithPrecision: '12302139' },
//   { x: 'Nov', title: 'Nov 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Dec', title: 'Dec 2024', y: null, totalAmountWithPrecision: '0.0' },
// ]

// const COMPARISON_LABOR_COST: PlotData[] = [
//   { x: 'Jan', title: 'Jan 2024', y: 7209619, totalAmountWithPrecision: '7209619' },
//   { x: 'Feb', title: 'Feb 2024', y: 16155095, totalAmountWithPrecision: '16155095' },
//   { x: 'Mar', title: 'Mar 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Apr', title: 'Apr 2024', y: 19462323, totalAmountWithPrecision: '19462323' },
//   { x: 'May', title: 'May 2024', y: 12694351, totalAmountWithPrecision: '12694351' },
//   { x: 'Jun', title: 'Jun 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Jul', title: 'Jul 2024', y: 7532221, totalAmountWithPrecision: '7532221' },
//   { x: 'Aug', title: 'Aug 2024', y: 10307260, totalAmountWithPrecision: '10307260' },
//   { x: 'Sep', title: 'Sep 2024', y: 8951513, totalAmountWithPrecision: '8951513' },
//   { x: 'Oct', title: 'Oct 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Nov', title: 'Nov 2024', y: null, totalAmountWithPrecision: '0.0' },
//   { x: 'Dec', title: 'Dec 2024', y: null, totalAmountWithPrecision: '0.0' },
// ]

// const COMPARISON_EMPLOYEE_EXPENSES: PlotData[] = [
//   { x: 'Jan', title: 'Jan 2024', y: 18450680, totalAmountWithPrecision: '18450680' },
//   { x: 'Feb', title: 'Feb 2024', y: 4205444, totalAmountWithPrecision: '4205444' },
//   { x: 'Mar', title: 'Mar 2024', y: 15640814, totalAmountWithPrecision: '15640814' },
//   { x: 'Apr', title: 'Apr 2024', y: 19739516, totalAmountWithPrecision: '19739516' },
//   { x: 'May', title: 'May 2024', y: 2823831, totalAmountWithPrecision: '2823831' },
//   { x: 'Jun', title: 'Jun 2024', y: 16321758, totalAmountWithPrecision: '16321758' },
//   { x: 'Jul', title: 'Jul 2024', y: 13094305, totalAmountWithPrecision: '13094305' },
//   { x: 'Aug', title: 'Aug 2024', y: 11531512, totalAmountWithPrecision: '11531512' },
//   { x: 'Sep', title: 'Sep 2024', y: 3522303, totalAmountWithPrecision: '3522303' },
//   { x: 'Oct', title: 'Oct 2024', y: 16115089, totalAmountWithPrecision: '16115089' },
//   { x: 'Nov', title: 'Nov 2024', y: 13062533, totalAmountWithPrecision: '13062533' },
//   { x: 'Dec', title: 'Dec 2024', y: 15960103, totalAmountWithPrecision: '15960103' },
// ]

// const TOTAL_EXPENSES = EMPLOYEE_EXPENSES.map((obj, idx) => {
//   return { ...obj, y: (obj.y || 0) + (LABOR_COST?.[idx]?.y || 0) }
// })

// const COMPARISON_TOTAL_EXPENSES = COMPARISON_EMPLOYEE_EXPENSES.map((obj, idx) => {
//   return { ...obj, y: (obj.y || 0) + (COMPARISON_LABOR_COST?.[idx]?.y || 0) }
// })

// export const DUMMY_DATA = {
//   LABOR_COST,
//   AGREEMENT_DATA,
//   TOTAL_EXPENSES,
//   EMPLOYEE_EXPENSES,
//   COMPARISON_LABOR_COST,
//   COMPARISON_TOTAL_EXPENSES,
//   COMPARISON_AGREEMENT_DATA,
//   COMPARISON_EMPLOYEE_EXPENSES,
// }
